import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  normalizeBusinessProfile,
  ONBOARDING_BUSINESS_PROFILE_OPTIONS
} from '../businessProfiles';
import {
  peekErpSessionActor,
  updateErpBusinessProfile
} from '../services/erpSessionClient';
import './FirstAccessTutorial.css';

export const TUTORIAL_VERSION = '2';
export const TUTORIAL_STORAGE_PREFIX = 'pontomais:first-access-tutorial';
export const TUTORIAL_SESSION_PREFIX = 'pontomais:first-access-tutorial-session';

export const DEFAULT_TUTORIAL_STEPS = Object.freeze([
  {
    id: 'empresa',
    icon: 'fa-store',
    title: 'Comece pelos dados da empresa',
    description: 'Confirme nome, documento, endereço e regime tributário. Esses dados aparecem nos relatórios e documentos fiscais.',
    checklist: ['Dados cadastrais', 'Endereço completo', 'Regime e natureza da operação'],
    route: '/configuracoes',
    targetSelector: '[data-tour="empresa"]'
  },
  {
    id: 'produto',
    icon: 'fa-box-open',
    title: 'Cadastre o primeiro produto',
    description: 'Informe nome, preço, unidade e estoque. Se houver emissão fiscal, peça ao contador os códigos tributários.',
    checklist: ['Preço de venda', 'Unidade correta', 'Estoque inicial'],
    route: '/produtos',
    targetSelector: '[data-tour="produto"]',
    module: 'inventory'
  },
  {
    id: 'caixa',
    icon: 'fa-cash-register',
    title: 'Abra o caixa antes de vender',
    description: 'Confira o operador e o valor inicial. Sangrias e suprimentos ficam registrados para o fechamento bater no fim do dia.',
    checklist: ['Operador', 'Saldo inicial', 'Formas de recebimento'],
    route: '/caixa',
    targetSelector: '[data-tour="caixa"]'
  },
  {
    id: 'venda',
    icon: 'fa-shopping-cart',
    title: 'Faça uma venda de teste',
    description: 'Adicione um item, confira o total e escolha a forma de pagamento. Você pode cancelar o teste depois.',
    checklist: ['Adicionar item', 'Conferir total', 'Finalizar pagamento'],
    route: '/pdv',
    targetSelector: '[data-tour="venda"]'
  },
  {
    id: 'backup',
    icon: 'fa-cloud-upload-alt',
    title: 'Proteja os dados do negócio',
    description: 'Ative a cópia externa e confira a última execução. O backup fora deste computador protege contra perda, roubo ou defeito.',
    checklist: ['Destino externo configurado', 'Última cópia conferida', 'Responsável definido'],
    route: '/configuracoes',
    targetSelector: '[data-tour="backup"]'
  }
]);

function normalizeIdentityPart(value, fallback) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
  return normalized || fallback;
}

export function tutorialStorageKey(user = {}) {
  const tenant = normalizeIdentityPart(user.tenant_id, 'tenant-local');
  const account = normalizeIdentityPart(user.id || user.username, 'usuario');
  return `${TUTORIAL_STORAGE_PREFIX}:v${TUTORIAL_VERSION}:${tenant}:${account}`;
}

export function tutorialSessionKey(user = {}) {
  return `${TUTORIAL_SESSION_PREFIX}:${tutorialStorageKey(user)}`;
}

export function readTutorialPreference(storage, user = {}) {
  if (!storage?.getItem) return null;
  try {
    const parsed = JSON.parse(storage.getItem(tutorialStorageKey(user)) || 'null');
    if (!parsed || parsed.version !== TUTORIAL_VERSION) return null;
    return parsed;
  } catch (_error) {
    return null;
  }
}

export function writeTutorialPreference(storage, user = {}, status, stepId = null) {
  if (!storage?.setItem) return null;
  const current = readTutorialPreference(storage, user) || {};
  const preference = {
    ...current,
    version: TUTORIAL_VERSION,
    status,
    stepId,
    updatedAt: new Date().toISOString()
  };
  storage.setItem(tutorialStorageKey(user), JSON.stringify(preference));
  return preference;
}

export function resetFirstAccessTutorial(storage, sessionStorageValue, user = {}) {
  storage?.removeItem?.(tutorialStorageKey(user));
  sessionStorageValue?.removeItem?.(tutorialSessionKey(user));
}

export function isTutorialEligible(user = {}) {
  const role = String(user.perfil_acesso || user.cargo || '').trim().toLowerCase();
  return role === 'admin' || role === 'owner' || role === 'manager';
}

export function canConfigureBusinessProfile(actor = {}) {
  const role = String(actor?.user?.role || actor?.role || '').trim().toLowerCase();
  return role === 'owner' || role === 'manager';
}

export function hasAuthoritativeBusinessProfile(actor = {}) {
  const value = actor?.tenant?.businessProfile ?? actor?.tenant?.business_profile;
  return Boolean(normalizeBusinessProfile(value, null));
}

export function shouldSelectBusinessProfile({ actor, preference } = {}) {
  return canConfigureBusinessProfile(actor)
    && !hasAuthoritativeBusinessProfile(actor)
    && preference?.businessProfileConfigured !== true;
}

export function tutorialStepsForActor(steps = DEFAULT_TUTORIAL_STEPS, actor = null) {
  const modules = actor?.tenant?.modules;
  return steps.filter((step) => (
    step
    && step.id
    && step.title
    && (!step.module || !modules || modules[step.module] !== false)
  ));
}

export function shouldShowFirstAccessTutorial({ user = {}, storage, sessionStorageValue, force = false } = {}) {
  if (!force && !isTutorialEligible(user)) return false;
  if (force) return true;
  const preference = readTutorialPreference(storage, user);
  if (preference?.status === 'completed' || preference?.status === 'dismissed') return false;
  return sessionStorageValue?.getItem?.(tutorialSessionKey(user)) !== 'skipped';
}

function currentPath() {
  return String(window.location.hash || '#/').replace(/^#/, '').split('?')[0] || '/';
}

export default function FirstAccessTutorial({
  user,
  steps = DEFAULT_TUTORIAL_STEPS,
  onNavigate,
  onClose,
  onComplete,
  force = false,
  storage = window.localStorage,
  sessionStorageValue = window.sessionStorage
}) {
  const centralActor = peekErpSessionActor();
  const storedPreference = useMemo(() => readTutorialPreference(storage, user), [storage, user]);
  const availableSteps = useMemo(
    () => tutorialStepsForActor(steps, centralActor),
    [centralActor, steps]
  );
  const storedIndex = Math.max(0, availableSteps.findIndex((step) => step.id === storedPreference?.stepId));
  const [stepIndex, setStepIndex] = useState(storedIndex);
  const [profileConfigured, setProfileConfigured] = useState(
    storedPreference?.businessProfileConfigured === true
  );
  const [selectedProfile, setSelectedProfile] = useState(
    () => normalizeBusinessProfile(
      centralActor?.tenant?.businessProfile ?? centralActor?.tenant?.business_profile,
      'retail'
    ) || 'retail'
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [visible, setVisible] = useState(() => shouldShowFirstAccessTutorial({
    user,
    storage,
    sessionStorageValue,
    force
  }));

  const step = availableSteps[stepIndex];
  const isLast = stepIndex === availableSteps.length - 1;
  const selectingBusinessProfile = visible && shouldSelectBusinessProfile({
    actor: centralActor,
    preference: { ...storedPreference, businessProfileConfigured: profileConfigured }
  });

  useEffect(() => {
    if (!visible || !step || selectingBusinessProfile) return undefined;

    writeTutorialPreference(storage, user, 'in_progress', step.id);
    if (step.route && currentPath() !== step.route) {
      if (typeof onNavigate === 'function') onNavigate(step.route, step);
      else window.location.hash = step.route;
    }

    let highlighted = null;
    const highlight = () => {
      if (!step.targetSelector) return;
      highlighted = document.querySelector(step.targetSelector);
      if (!highlighted) return;
      highlighted.classList.add('pm-tour-target');
      highlighted.setAttribute('data-tour-active', 'true');
      highlighted.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    };
    const timer = window.setTimeout(highlight, 240);

    return () => {
      window.clearTimeout(timer);
      if (highlighted) {
        highlighted.classList.remove('pm-tour-target');
        highlighted.removeAttribute('data-tour-active');
      }
    };
  }, [onNavigate, selectingBusinessProfile, step, storage, user, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !selectingBusinessProfile) skipForNow();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
    // A função usa somente estado e storages estáveis desta renderização.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectingBusinessProfile, visible]);

  if (!visible || ((!step || availableSteps.length === 0) && !selectingBusinessProfile)) return null;

  async function saveBusinessProfile() {
    if (!selectedProfile || savingProfile) return;
    setSavingProfile(true);
    setProfileError('');
    try {
      const refreshedActor = await updateErpBusinessProfile(selectedProfile);
      const preference = writeTutorialPreference(
        storage,
        user,
        'in_progress',
        availableSteps[0]?.id || null
      );
      storage?.setItem?.(tutorialStorageKey(user), JSON.stringify({
        ...preference,
        businessProfileConfigured: true,
        businessProfile: refreshedActor?.tenant?.businessProfile || 'retail'
      }));
      setProfileConfigured(true);
      setStepIndex(0);
      window.location.reload();
    } catch (error) {
      setProfileError(error?.message || 'Não foi possível configurar o perfil da empresa.');
      setSavingProfile(false);
    }
  }

  function finish(status) {
    writeTutorialPreference(storage, user, status, status === 'completed' ? availableSteps.at(-1)?.id : step.id);
    setVisible(false);
    if (status === 'completed') onComplete?.();
    onClose?.(status);
  }

  function skipForNow() {
    sessionStorageValue?.setItem?.(tutorialSessionKey(user), 'skipped');
    setVisible(false);
    onClose?.('skipped');
  }

  function next() {
    if (isLast) return finish('completed');
    setStepIndex((current) => Math.min(current + 1, availableSteps.length - 1));
  }

  if (selectingBusinessProfile) {
    return createPortal(
      <>
        <div className="pm-tour-backdrop" aria-hidden="true" />
        <section
          className="pm-tour-dialog pm-profile-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pm-profile-title"
        >
          <header className="pm-tour-header">
            <div className="pm-tour-heading">
              <span className="pm-tour-icon" aria-hidden="true"><i className="fas fa-sliders-h" /></span>
              <div>
                <span className="pm-tour-eyebrow">Configuração inicial · Perfil da empresa</span>
                <h2 id="pm-profile-title">Como sua empresa opera?</h2>
              </div>
            </div>
          </header>

          <div className="pm-tour-body pm-profile-body">
            <p>
              Escolha o segmento para o Nivora mostrar apenas os módulos que fazem sentido.
              Você poderá alterar essa configuração depois com um acesso de gestão.
            </p>
            <div className="pm-profile-options" role="radiogroup" aria-label="Segmento da empresa">
              {ONBOARDING_BUSINESS_PROFILE_OPTIONS.map((option) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedProfile === option.value}
                  className={`pm-profile-option ${selectedProfile === option.value ? 'is-selected' : ''}`}
                  key={option.value}
                  onClick={() => setSelectedProfile(option.value)}
                  disabled={savingProfile}
                >
                  <span className="pm-profile-option-icon" aria-hidden="true">
                    <i className={`fas ${option.icon}`} />
                  </span>
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                  <i className="fas fa-check-circle pm-profile-option-check" aria-hidden="true" />
                </button>
              ))}
            </div>
            {profileError && <div className="pm-profile-error" role="alert">{profileError}</div>}
          </div>

          <footer className="pm-tour-footer pm-profile-footer">
            <small>Somente owner ou manager pode confirmar esta escolha.</small>
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveBusinessProfile}
              disabled={!selectedProfile || savingProfile}
            >
              {savingProfile
                ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Configurando...</>
                : <>Aplicar perfil e continuar <i className="fas fa-arrow-right" aria-hidden="true" /></>}
            </button>
          </footer>
        </section>
      </>,
      document.body
    );
  }

  return createPortal(
    <>
      <div className="pm-tour-backdrop" aria-hidden="true" />
      <section className="pm-tour-dialog" role="dialog" aria-modal="true" aria-labelledby="pm-tour-title">
        <header className="pm-tour-header">
          <div className="pm-tour-heading">
            <span className="pm-tour-icon" aria-hidden="true"><i className={`fas ${step.icon || 'fa-compass'}`} /></span>
            <div>
              <span className="pm-tour-eyebrow">Primeiros passos · {stepIndex + 1} de {availableSteps.length}</span>
              <h2 id="pm-tour-title">{step.title}</h2>
            </div>
          </div>
          <button type="button" className="pm-tour-close" onClick={skipForNow} aria-label="Pular tutorial por enquanto">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </header>

        <div
          className="pm-tour-progress"
          aria-label={`Etapa ${stepIndex + 1} de ${availableSteps.length}`}
          style={{ gridTemplateColumns: `repeat(${availableSteps.length}, minmax(0, 1fr))` }}
        >
          {availableSteps.map((item, index) => (
            <span key={item.id} className={index <= stepIndex ? 'is-complete' : ''} />
          ))}
        </div>

        <div className="pm-tour-body">
          <p>{step.description}</p>
          {step.checklist?.length > 0 && (
            <ul>
              {step.checklist.map((item) => <li key={item}><i className="fas fa-check" aria-hidden="true" /> {item}</li>)}
            </ul>
          )}
          <small>Você pode seguir o passo indicado na tela ou avançar para conhecer o próximo.</small>
        </div>

        <footer className="pm-tour-footer">
          <div className="pm-tour-dismiss-actions">
            <button type="button" className="pm-tour-link" onClick={skipForNow}>Pular por enquanto</button>
            <button type="button" className="pm-tour-link is-danger" onClick={() => finish('dismissed')}>Nunca mais exibir</button>
          </div>
          <div className="pm-tour-step-actions">
            {stepIndex > 0 && (
              <button type="button" className="btn btn-outline" onClick={() => setStepIndex((current) => Math.max(0, current - 1))}>
                <i className="fas fa-arrow-left" aria-hidden="true" /> Voltar
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={next}>
              {isLast ? <><i className="fas fa-check" aria-hidden="true" /> Concluir</> : <>Avançar <i className="fas fa-arrow-right" aria-hidden="true" /></>}
            </button>
          </div>
        </footer>
      </section>
    </>,
    document.body
  );
}
